import { FormGroup, InputField } from "@bokio/designsystem/components/Form";
import { Notice } from "@bokio/designsystem/components/Notice/Notice";
import { Link } from "@bokio/elements/Link/Link";
import VatSettingAccountingTypeInput from "@bokio/guest/src/scenes/SignUp/components/SignUpCustomizeCompanySettings/components/VatSettingAccountingTypeInput/VatSettingAccountingTypeInput";
import { GeneralLangFactory, SettingsLangFactory, SignUpLangFactory } from "@bokio/lang";
import AccountingLangFactory from "@bokio/lang/AccountingLangFactory";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { FiscalYearTurnoverPicker } from "@bokio/settings/src/scenes/FiscalYears/components/FiscalYearTurnoverPicker";
import { FiscalYearValidator } from "@bokio/settings/src/scenes/FiscalYears/fiscalYearValidator";
import { getRoute } from "@bokio/shared/route";
import { noop } from "@bokio/shared/utils";
import { formatDate, formatMessage } from "@bokio/shared/utils/format";
import NP from "@bokio/utils/NP";

import type { FiscalYearFormData } from "@bokio/settings/src/scenes/FiscalYears/components/FiscalYearForm";

import * as styles from "../addFiscalYearModal.scss";

type CompanyInfo = m.Settings.Controllers.CompanyInfoForView;
import FiscalYearStatus = m.Core.FiscalYearStatus;
interface AddFiscalYearModalViewProps {
	formData: FiscalYearFormData;
	companyInfo: CompanyInfo;
	onUpdate: (data: FiscalYearFormData) => void;
	showSelectCompanyLink: boolean;
}

export const AddFiscalYearModalView = ({
	formData,
	companyInfo,
	onUpdate,
	showSelectCompanyLink,
}: AddFiscalYearModalViewProps) => {
	const generalLang = GeneralLangFactory();
	const accountingLang = AccountingLangFactory();
	const settingsLang = SettingsLangFactory();
	const signUpLang = SignUpLangFactory();
	const validator = new FiscalYearValidator(generalLang, accountingLang, settingsLang);
	const validation = validator.validate(formData);

	const shouldRenderTurnover = companyInfo.Country === m.CountryCode.SE;

	const description =
		companyInfo.FiscalYearStatus === FiscalYearStatus.PromptToCreateNextYear
			? settingsLang.AddFiscalYearModal_CreateUpcomming_Description
			: settingsLang.FiscalYears_NoActiveSettings_Message;
	return (
		<div className={styles.container} data-testid="AddFiscalYear_CreateView">
			<p className={styles.description}>{description}</p>
			<div className={styles.datesContainer}>
				<FormGroup className={styles.date}>
					<InputField
						disabled={true}
						label={generalLang.Startdate}
						value={NP(formData.StartDate, formatDate)}
						onChange={noop}
						errors={validator.filterByField("StartDate", validation.errors)}
					/>
				</FormGroup>
				<FormGroup className={styles.date}>
					<InputField
						disabled={true}
						label={generalLang.Enddate}
						value={NP(formData.EndDate, formatDate)}
						onChange={noop}
						errors={validator.filterByField("EndDate", validation.errors)}
					/>
				</FormGroup>
			</div>
			<p className={styles.inputInfo}>{settingsLang.FiscalYears_LockedDates_Explanation}</p>

			<VatSettingAccountingTypeInput
				company={companyInfo}
				state={{ accountingType: formData.AccountingForm, vatSetting: companyInfo.ActiveVatSetting }}
				onStateChange={s => onUpdate({ ...formData, AccountingForm: s.accountingType })}
				accountingTypeDescription={
					<p className={styles.inputInfo}>
						{formatMessage(settingsLang.FiscalYears_AccountingMethod_Description, linkLabel => (
							<Link external={signUpLang.GovernmentCompanyInfoUrl} style="normal" target="_blank">
								{linkLabel}
							</Link>
						))}
					</p>
				}
				validation={validation}
				validator={validator}
			/>
			{shouldRenderTurnover && (
				<FormGroup>
					<FiscalYearTurnoverPicker
						turnover={formData.ExpectedTurnover ?? 0}
						onChange={(val: number) => onUpdate({ ...formData, ExpectedTurnover: val })}
					/>
				</FormGroup>
			)}
			{showSelectCompanyLink && (
				<Link route={getRoute("selectCompany")}> {settingsLang.AddFiscalYearModal_SwitchCompanies} </Link>
			)}
			{/* // We can't link to the Vat settings page because this is rendered in a blocking modal. */}
			<Notice color="info">{formatMessage(settingsLang.FiscalYears_VatSettingsMoved, linkLabel => linkLabel)}</Notice>
		</div>
	);
};
